import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HardHat, ArrowLeft, Shield, AlertTriangle } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import SEO from '../components/SEO';

const H2 = ({ children, id }) => (
  <h2 id={id} className="text-xl font-bold text-slate-900 mt-10 mb-4 scroll-mt-24">{children}</h2>
);
const H3 = ({ children }) => (
  <h3 className="text-lg font-bold text-slate-800 mt-6 mb-3">{children}</h3>
);

const CaliforniaPrivacyPage = () => {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title="California Consumer Privacy Notice"
        description="California residents — your CCPA / CPRA privacy rights at ContractorsLab. Access, delete, correct, port, opt out, or limit use of your personal information."
        keywords="ContractorsLab CCPA, CPRA, California privacy rights, do not sell, do not share"
        path="/california-privacy"
      />
      {/* Header */}
      <header className="bg-slate-900 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-9 h-9 bg-orange-600 rounded-sm flex items-center justify-center">
                <HardHat className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-black text-white tracking-tight hidden sm:block">CONTRACTORSLAB</span>
            </Link>
            <div className="flex items-center gap-3">
              <LanguageSwitcher variant="dark" />
              <Link to="/" className="text-slate-400 hover:text-white flex items-center gap-2 text-sm">
                <ArrowLeft className="w-4 h-4" /> {t('legal.backHome')}
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 md:px-8 py-12">
        <article className="prose prose-slate max-w-none">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-slate-900 text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm mb-4">
            California Residents
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-slate-900 mb-2">
            California Consumer Privacy Notice
          </h1>
          <p className="text-slate-500 mb-8">Last Updated: January 19, 2026 · Effective: February 1, 2026</p>

          {/* Summary card */}
          <div className="bg-slate-100 border-l-4 border-slate-400 p-5 mb-8 rounded-sm">
            <div className="flex gap-3">
              <Shield className="w-5 h-5 text-slate-700 shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-slate-900 mb-1">At-a-glance for California residents</p>
                <p className="text-sm text-slate-700 leading-relaxed m-0">
                  Under the California Consumer Privacy Act ("CCPA"), as amended by the California Privacy
                  Rights Act ("CPRA"), California residents have specific rights regarding their personal
                  information. We do <strong>not sell</strong> personal information and do <strong>not share</strong>{' '}
                  it for cross-context behavioral advertising. You may access, correct, delete, export your
                  data, limit use of sensitive information, and opt out of any sale or sharing — free of
                  charge and without any change in quality of service.
                </p>
              </div>
            </div>
          </div>

          <p className="lead text-lg text-slate-700">
            This California Consumer Privacy Notice ("CA Notice") supplements our general{' '}
            <Link to="/privacy" className="text-orange-600 font-medium">Privacy Policy</Link> and applies
            solely to California residents ("Consumers"). It is published by <strong>Retention Logic
            LLC</strong>, a limited liability company organized under the laws of the
            <strong> Commonwealth of Puerto Rico</strong>, which creates, owns, and operates
            ContractorsLab.com. It describes the categories of personal information we collect, the
            purposes for which we use and disclose it, and the rights Consumers have under the CCPA,
            CPRA, and related regulations issued by the California Privacy Protection Agency
            ("CPPA"). Capitalized terms not defined here have the meanings given in our Privacy
            Policy or in the CCPA.
          </p>

          <H2 id="scope">1. Who This Notice Applies To</H2>
          <p>
            This Notice applies to any natural person residing in California whose personal information
            we process, including visitors, Clients, Contractors, job applicants, and others interacting
            with our Service. If you are not a California resident, see the{' '}
            <Link to="/privacy" className="text-orange-600 font-medium">Privacy Policy</Link>. Employment
            and B2B information previously subject to partial exemptions are covered by this Notice in
            full since January 1, 2023.
          </p>

          <H2 id="categories">2. Categories of Personal Information We Collect</H2>
          <p>
            In the preceding twelve (12) months, we have collected the following categories of personal
            information listed in California Civil Code § 1798.140(v):
          </p>
          <div className="overflow-x-auto my-4">
            <table className="w-full text-sm border-collapse">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left p-3 border border-slate-200">Category</th>
                  <th className="text-left p-3 border border-slate-200">Examples</th>
                  <th className="text-left p-3 border border-slate-200">Collected?</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="p-3 border border-slate-200">A. Identifiers</td>
                  <td className="p-3 border border-slate-200">Name, email, phone, account ID, IP address</td>
                  <td className="p-3 border border-slate-200">Yes</td>
                </tr>
                <tr>
                  <td className="p-3 border border-slate-200">B. Customer records (Cal. Civ. Code § 1798.80(e))</td>
                  <td className="p-3 border border-slate-200">Mailing address, signature, payment info (via Stripe)</td>
                  <td className="p-3 border border-slate-200">Yes</td>
                </tr>
                <tr>
                  <td className="p-3 border border-slate-200">C. Protected classifications</td>
                  <td className="p-3 border border-slate-200">Age (18+ confirmation), gender</td>
                  <td className="p-3 border border-slate-200">Not collected (18+ attestation only)</td>
                </tr>
                <tr>
                  <td className="p-3 border border-slate-200">D. Commercial information</td>
                  <td className="p-3 border border-slate-200">Jobs posted, proposals submitted, Wallet transactions</td>
                  <td className="p-3 border border-slate-200">Yes</td>
                </tr>
                <tr>
                  <td className="p-3 border border-slate-200">E. Biometric information</td>
                  <td className="p-3 border border-slate-200">Fingerprints, voiceprints, faceprints</td>
                  <td className="p-3 border border-slate-200">No</td>
                </tr>
                <tr>
                  <td className="p-3 border border-slate-200">F. Internet / electronic activity</td>
                  <td className="p-3 border border-slate-200">Browsing, click-through, interaction with Service</td>
                  <td className="p-3 border border-slate-200">Yes</td>
                </tr>
                <tr>
                  <td className="p-3 border border-slate-200">G. Geolocation (precise)</td>
                  <td className="p-3 border border-slate-200">GPS location</td>
                  <td className="p-3 border border-slate-200">Only with express mobile permission</td>
                </tr>
                <tr>
                  <td className="p-3 border border-slate-200">G. Geolocation (approximate)</td>
                  <td className="p-3 border border-slate-200">Zip code, IP-derived city</td>
                  <td className="p-3 border border-slate-200">Yes</td>
                </tr>
                <tr>
                  <td className="p-3 border border-slate-200">H. Audio, electronic, visual</td>
                  <td className="p-3 border border-slate-200">Photos uploaded to jobs, support-call recordings</td>
                  <td className="p-3 border border-slate-200">Yes (only where disclosed and permitted)</td>
                </tr>
                <tr>
                  <td className="p-3 border border-slate-200">I. Professional / employment-related</td>
                  <td className="p-3 border border-slate-200">Contractor license, insurance, services offered</td>
                  <td className="p-3 border border-slate-200">Yes (Contractors only)</td>
                </tr>
                <tr>
                  <td className="p-3 border border-slate-200">J. Education information</td>
                  <td className="p-3 border border-slate-200">Degree, school</td>
                  <td className="p-3 border border-slate-200">No</td>
                </tr>
                <tr>
                  <td className="p-3 border border-slate-200">K. Inferences</td>
                  <td className="p-3 border border-slate-200">Category preferences, lead-quality scores</td>
                  <td className="p-3 border border-slate-200">Yes (limited operational inferences only)</td>
                </tr>
                <tr>
                  <td className="p-3 border border-slate-200">L. Sensitive personal information</td>
                  <td className="p-3 border border-slate-200">Government ID (SSN/EIN for 1099), account credentials, precise geolocation, payment tokens</td>
                  <td className="p-3 border border-slate-200">Yes (only as strictly necessary — see § 6)</td>
                </tr>
              </tbody>
            </table>
          </div>

          <H2 id="sources">3. Sources of Personal Information</H2>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Directly from you:</strong> when you register, post a job, submit a proposal, top up your wallet, or send messages.</li>
            <li><strong>Automatically:</strong> through cookies, server logs, and SDKs as described in § 7 of the Privacy Policy.</li>
            <li><strong>From third parties:</strong> identity and license-verification services, OAuth providers (Google via Emergent), payment processors (Stripe), fraud-prevention services, analytics providers, and public records (state license databases).</li>
            <li><strong>From other Users:</strong> reviews, contact-reveal requests, and in-app messages.</li>
          </ul>

          <H2 id="purposes">4. Business and Commercial Purposes</H2>
          <p>We collect and use personal information for the following business or commercial purposes, as defined in CPRA § 1798.140(e):</p>
          <ol className="list-decimal pl-6 space-y-2">
            <li>Performing services you request — account setup, Lead matching, messaging, Wallet management.</li>
            <li>Processing transactions — Wallet top-ups, Contact Reveal Fees, refunds.</li>
            <li>Verifying Contractor credentials — license and insurance validation, identity verification.</li>
            <li>Detecting and preventing fraud, abuse, and security incidents.</li>
            <li>Debugging, error correction, and quality assurance.</li>
            <li>Short-term, transient use (e.g., rendering a page, completing a request).</li>
            <li>Legal compliance — tax reporting, responding to subpoenas, consumer protection laws.</li>
            <li>Internal research and analytics to improve and develop the Service.</li>
            <li>Communicating with you (transactional) and, where opted in, marketing.</li>
            <li>Enforcing Terms and protecting rights, property, and safety.</li>
          </ol>

          <H2 id="disclosures">5. Categories of Third Parties to Whom We Disclose</H2>
          <p>
            Within the last twelve (12) months, we have disclosed the categories of personal information
            listed in § 2 to the following categories of recipients, for the business purposes listed in §
            4:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Payment processors (Stripe) — to process transactions.</li>
            <li>Cloud hosting and database providers — to operate the Service.</li>
            <li>Cloud storage (AWS S3) — to securely store Contractor license/insurance documents.</li>
            <li>Email (SparkPost) and SMS (Infobip) delivery providers — to send opted-in messages.</li>
            <li>Analytics and fraud-prevention service providers — for security and quality.</li>
            <li>Identity- and license-verification partners — for Contractor verification.</li>
            <li>AI providers (OpenAI, Anthropic, Google via Emergent) — strictly for generating educational tips content; no personal information is sent to these providers.</li>
            <li>Professional advisors (auditors, lawyers, accountants) — on a need-to-know basis.</li>
            <li>Government authorities — where required by law or to protect rights.</li>
            <li>Other Users of the Service — in limited contexts (e.g., Client profile visible to matched Contractors).</li>
          </ul>

          <H2 id="sensitive">6. Sensitive Personal Information</H2>
          <p>
            We collect the following categories of sensitive personal information as defined in CPRA §
            1798.140(ae), only for permitted business purposes described in 11 CCR § 7027(m):
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Account log-in credentials</strong> (email + password hash) — to authenticate you.</li>
            <li><strong>Government identification numbers</strong> — EIN/SSN collected solely for tax reporting obligations (e.g., Form 1099-K), W-9 collection, or government-mandated identity verification of Contractors.</li>
            <li><strong>Payment account information</strong> — tokenized and handled directly by Stripe; we do not retain full card numbers or CVV.</li>
            <li><strong>Precise geolocation</strong> — only where you explicitly grant mobile permission; used to match Contractors to nearby jobs.</li>
          </ul>
          <p>
            We do <strong>not</strong> use sensitive personal information to infer characteristics about
            Consumers. Therefore, the CPRA "right to limit use of sensitive information" is largely
            inapplicable to our practices, but you may still request to restrict uses by submitting a
            request through our <Link to="/contact" className="text-orange-600 font-medium">Contact page</Link>.
          </p>

          <H2 id="retention">7. Retention Periods</H2>
          <p>We retain each category of personal information only as long as reasonably necessary:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Account data: life of account + 7 years (tax and legal-hold).</li>
            <li>Transactional data: 7 years.</li>
            <li>Messages: 3 years after last activity.</li>
            <li>Security logs: up to 24 months.</li>
            <li>Marketing preferences: until withdrawn + evidence of withdrawal.</li>
            <li>Reviews: indefinitely (to preserve historical accuracy).</li>
          </ul>
          <p>Longer retention may apply for legal holds or disputes.</p>

          <H2 id="sale-sharing">8. Sale &amp; Sharing of Personal Information</H2>
          <p className="bg-green-50 border-l-4 border-green-600 p-4 rounded-sm font-medium text-slate-800">
            ContractorsLab does <strong>NOT</strong> sell personal information for monetary or other
            valuable consideration. We do <strong>NOT</strong> share personal information for
            cross-context behavioral advertising. We have <strong>not</strong> sold or shared the personal
            information of any California Consumer — including any minor under 16 — in the preceding
            twelve (12) months.
          </p>
          <p>
            Although we do not engage in sale or sharing, you retain the right to opt out at any time,
            including through the Global Privacy Control ("GPC") browser signal, which we honor
            automatically for California residents.
          </p>

          <H2 id="your-rights">9. Your California Privacy Rights</H2>
          <p>Subject to certain exceptions, California residents have the following rights:</p>

          <H3>9.1 Right to Know</H3>
          <p>
            You may request that we disclose: (a) the categories and specific pieces of personal
            information we have collected about you; (b) the categories of sources; (c) the business or
            commercial purposes for collecting or disclosing; (d) the categories of third parties to whom
            we disclose personal information; and (e) the specific pieces of personal information we have
            about you. We respond within 45 days (extendable by 45 with notice).
          </p>

          <H3>9.2 Right to Delete</H3>
          <p>
            You may request that we delete personal information we have collected about you, subject to
            exceptions including where we need the information to complete a transaction, detect fraud,
            comply with legal obligations, or exercise free speech.
          </p>

          <H3>9.3 Right to Correct</H3>
          <p>
            You may request that we correct inaccurate personal information we maintain about you. We
            will use commercially reasonable efforts to correct inaccuracies after considering the nature
            and purpose of the data.
          </p>

          <H3>9.4 Right to Portability</H3>
          <p>
            You may request a copy of your personal information in a portable, structured, commonly used,
            machine-readable format so you can transmit it to another business.
          </p>

          <H3>9.5 Right to Opt Out of Sale or Sharing</H3>
          <p>
            Although we do not sell or share personal information, you may still submit an opt-out
            request at any time through our <Link to="/contact" className="text-orange-600 font-medium">Contact page</Link>{' '}
            or by enabling the Global Privacy Control signal in your browser. We will also honor a
            persistent "Do Not Sell or Share My Personal Information" preference set via our cookie
            banner, when displayed.
          </p>

          <H3>9.6 Right to Limit Use of Sensitive Personal Information</H3>
          <p>
            You may request that we limit our use or disclosure of sensitive personal information to
            uses necessary to perform the Service, as provided under CPRA § 1798.121 and 11 CCR § 7027.
            Because we already restrict such use to strictly necessary operational purposes, this right
            does not meaningfully change our processing for most Users.
          </p>

          <H3>9.7 Right to Non-Discrimination</H3>
          <p>
            We will not discriminate against you for exercising any right under the CCPA/CPRA. We will
            not deny Service, charge different prices, provide a different level of quality, or suggest
            any of the above as a result of your exercise of rights.
          </p>

          <H3>9.8 Right to Appeal (Effective Denials)</H3>
          <p>
            If we deny your request, you may appeal the denial by replying to our response or submitting
            a new request through our <Link to="/contact" className="text-orange-600 font-medium">Contact page</Link>{' '}
            with the subject line "CCPA Appeal." We will respond to appeals within 60 days.
          </p>

          <H2 id="how-to-submit">10. How to Submit a Request</H2>
          <p>Submit any Consumer-rights request by either of the following methods:</p>
          <ol className="list-decimal pl-6 space-y-2">
            <li>Visit our <Link to="/contact" className="text-orange-600 font-medium">Contact page</Link>, select <strong>Legal / Compliance</strong>, and describe the request (e.g., "Right to Know," "Right to Delete").</li>
            <li>Email us through the address listed on our Contact page during regular business hours.</li>
          </ol>
          <p>
            We will acknowledge receipt within ten (10) business days and respond substantively within
            forty-five (45) days. Where additional time is required due to complexity or volume, we will
            notify you and may take up to ninety (90) days in total.
          </p>

          <H3>10.1 Verification</H3>
          <p>
            To protect your account, we verify identity before fulfilling a request. For Account holders,
            we may verify by requiring you to log in, confirm access to your Account email, and/or
            provide two additional data points that match our records. For non-Account holders, we may
            request additional documentation reasonable to the sensitivity of the data and the request.
          </p>

          <H3>10.2 Authorized Agents</H3>
          <p>
            You may designate an authorized agent to act on your behalf. We require: (a) the agent's
            signed authorization or power of attorney; (b) proof of the agent's identity; and (c) direct
            confirmation from you (the Consumer) that you authorized the request. An attorney acting on
            your behalf may substitute proof of representation for (a) and (c).
          </p>

          <H3>10.3 No Fee</H3>
          <p>
            We will not charge a fee to process or respond to a verifiable Consumer request unless it is
            excessive, repetitive, or manifestly unfounded. If we determine a fee is warranted, we will
            provide you with a cost estimate before completing the request.
          </p>

          <H2 id="financial-incentives">11. Notice of Financial Incentives</H2>
          <p>
            At this time, ContractorsLab does <strong>not</strong> offer any financial incentives,
            price/service differences, or loyalty programs in exchange for the retention or sale of
            personal information. If we introduce one in the future, we will update this Notice and
            obtain the necessary opt-in consent before enrolling you.
          </p>

          <H2 id="shine-the-light">12. Shine the Light</H2>
          <p>
            California Civil Code § 1798.83 permits California residents to request a list of the
            categories of personal information we may have disclosed to third parties for those third
            parties' direct-marketing purposes during the prior calendar year. We do not share personal
            information in this manner. To confirm or request further detail, contact us through the{' '}
            <Link to="/contact" className="text-orange-600 font-medium">Contact page</Link>.
          </p>

          <H2 id="ca-minors">13. California Minors</H2>
          <p>
            The Service is not directed to individuals under 18. If you are a California resident between
            13 and 17 and have inadvertently created an account, you or your parent/guardian may request
            removal of any publicly-posted content by contacting us. We will remove such content from
            public view (though residual copies in backups or archives may remain subject to our
            retention schedule).
          </p>

          <H2 id="metrics">14. CCPA Request Metrics</H2>
          <p>
            Businesses meeting the statutory threshold must publish annual request metrics. Once our
            Service reaches that threshold, we will publish in this Notice the number of requests to
            know, delete, correct, opt out of sale/sharing, and limit use of sensitive information
            received, complied with (in whole or in part), and denied — along with the median and mean
            number of days we took to respond. Current period metrics: <strong>insufficient threshold
            reached; disclosure will be added in the first full calendar year we meet the threshold</strong>.
          </p>

          <H2 id="gpc">15. Global Privacy Control &amp; Cookie-Level Signals</H2>
          <p>
            We honor the Global Privacy Control (GPC) signal, a browser-based or extension-based opt-out
            preference. When detected, we will treat the signal as a valid request to opt out of the sale
            or sharing of your personal information, which also applies to any advertising identifiers we
            may use. We do not honor "Do Not Track" headers because there is no industry consensus on
            their interpretation.
          </p>

          <H2 id="updates">16. Updates to This Notice</H2>
          <p>
            We will update this Notice at least every twelve (12) months, as required by CCPA, and
            whenever our practices materially change. Material changes will be announced at least thirty
            (30) days in advance by email or in-app notice. The "Last Updated" date above reflects the
            most recent revision.
          </p>

          <H2 id="accessibility">17. Accessibility of This Notice</H2>
          <p>
            This Notice is available in accessible formats. If you need assistance reading or
            understanding this Notice, including in an alternate format, please contact us through our{' '}
            <Link to="/contact" className="text-orange-600 font-medium">Contact page</Link> or by phone
            through your California Relay Service provider.
          </p>

          <H2 id="contact">18. Contact Us</H2>
          <div className="bg-slate-100 p-5 rounded-sm">
            <div className="flex gap-3">
              <AlertTriangle className="w-5 h-5 text-slate-600 shrink-0 mt-0.5" />
              <div className="m-0">
                <p className="font-bold text-slate-900 mb-1">To exercise any right described above:</p>
                <p className="text-sm text-slate-700 m-0">
                  Go to our <Link to="/contact" className="text-orange-600 font-medium">Contact page</Link>,
                  select <strong>Legal / Compliance</strong>, and clearly identify which right you are
                  exercising. Include your Account email and any information that helps us verify your
                  identity.
                </p>
              </div>
            </div>
          </div>
          <p className="mt-5">
            For additional information about your rights, you may also contact the California Privacy
            Protection Agency at <a href="https://cppa.ca.gov" target="_blank" rel="noopener noreferrer" className="text-orange-600 font-medium">cppa.ca.gov</a>{' '}
            or the Office of the California Attorney General at{' '}
            <a href="https://oag.ca.gov/privacy" target="_blank" rel="noopener noreferrer" className="text-orange-600 font-medium">oag.ca.gov/privacy</a>.
          </p>

          <div className="mt-12 pt-8 border-t border-slate-200">
            <p className="text-sm text-slate-500">
              This California Consumer Privacy Notice supplements our general{' '}
              <Link to="/privacy" className="text-orange-600 font-medium">Privacy Policy</Link>. In the
              event of any conflict, this Notice controls for California residents only.
            </p>
          </div>
        </article>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 py-8">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-orange-600 rounded-sm flex items-center justify-center">
                <HardHat className="w-5 h-5 text-white" />
              </div>
              <span className="font-black text-white">CONTRACTORSLAB</span>
            </div>
            <div className="flex items-center gap-6 text-slate-400 text-sm">
              <Link to="/terms" className="hover:text-white">Terms</Link>
              <Link to="/privacy" className="hover:text-white">Privacy</Link>
              <Link to="/california-privacy" className="hover:text-white">CA Privacy</Link>
              <Link to="/sms-terms" className="hover:text-white">SMS Terms</Link>
              <Link to="/contact" className="hover:text-white">Contact</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CaliforniaPrivacyPage;
